/**
 * Beardbrand cross-product selector
 *
 * Keeps sibling product links aligned with the shopper's current native option
 * selection (Size by default). Navigation always goes to the sibling product's
 * real Shopify URL; no PDP HTML is replaced in-place.
 *
 * Preferred path:
 * - Build/update the sibling link with the matching ?variant= ID.
 *
 * Resilience fallback:
 * - Store the preserved option briefly in sessionStorage before cross-product
 *   navigation.
 * - If the destination product does not arrive on that option for any reason,
 *   re-apply it through Split's native variant control, which lets Split update
 *   the product form, URL, price, availability and related variant state.
 *
 * Perceived-performance enhancement:
 * - After the current PDP finishes loading, prefetch all sibling PDP documents
 *   at low priority during browser idle time, staggered to avoid a request burst.
 * - Hover, focus or touch still prefetches the intended fragrance immediately.
 * - Background prefetch is skipped when the browser reports Data Saver or a
 *   very slow connection. Normal navigation always remains intact.
 *
 * Navigation-continuity enhancement:
 * - When a shopper has meaningfully scrolled and switches fragrances in the
 *   same tab, remember where the selector sat in the viewport and restore that
 *   position immediately on the destination PDP. At/near the top of the page,
 *   leave native scroll position alone and let the named View Transition animate
 *   the selector between its natural positions instead of forcing a scroll jump.
 *
 * -Jenny 10/8/26
 */

(() => {
  if (customElements.get('product-group-selector')) return;

  const STORAGE_KEY = 'bb-product-group-preserved-option';
  const STORAGE_MAX_AGE = 60 * 1000;
  const SCROLL_STORAGE_KEY = 'bb-product-group-scroll-position';
  const SCROLL_STORAGE_MAX_AGE = 15 * 1000;
  const SCROLL_TOLERANCE = 2;
  const SCROLL_MIN_Y = 96;
  const BACKGROUND_PREFETCH_DELAY = 1000;
  const BACKGROUND_PREFETCH_STAGGER = 250;
  const VARIANT_PREFETCH_DELAY = 600;

  class ProductGroupSelector extends HTMLElement {
    connectedCallback() {
      this.productPage = this.closest('product-page');
      this.links = Array.from(this.querySelectorAll('[data-product-group-link]'));
      this.variantMap = this.getVariantMap();
      this.prefetchedUrls = new Set();
      this.backgroundPrefetchTimers = new Set();

      this.handleProductChange = this.onProductChange.bind(this);
      this.handleVariantChange = this.scheduleUpdate.bind(this);
      this.handleLinkClick = this.onLinkClick.bind(this);
      this.handleLinkIntent = this.onLinkIntent.bind(this);
      this.handleDeferredInit = this.deferredInit.bind(this);
      this.handleWindowLoad = () => this.scheduleBackgroundPrefetch();
      this.handleScrollRestoreLoad = () => {
        this.applyNavigationPosition(true);
      };

      if (this.productPage) {
        this.productPage.addEventListener('change', this.handleProductChange);
      }

      this.addEventListener('click', this.handleLinkClick);
      this.addEventListener('pointerover', this.handleLinkIntent);
      this.addEventListener('focusin', this.handleLinkIntent);
      this.addEventListener('touchstart', this.handleLinkIntent, { passive: true });

      this.deferredInit();
      requestAnimationFrame(this.handleDeferredInit);
      this.scheduleNavigationPositionRestore();

      // Let the current PDP win the network first. Once it has fully loaded,
      // quietly warm the browser cache with the sibling PDP documents.
      if (document.readyState === 'complete') {
        this.scheduleBackgroundPrefetch();
      } else {
        window.addEventListener('load', this.handleWindowLoad, { once: true });
      }

      // Wait until Split's native product-variants element is upgraded before
      // attempting the navigation fallback on the destination PDP.
      customElements.whenDefined('product-variants').then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.restorePreservedSelection());
        });
      });
    }

    disconnectedCallback() {
      if (this.productPage && this.handleProductChange) {
        this.productPage.removeEventListener('change', this.handleProductChange);
      }

      if (this.productVariants && this.handleVariantChange) {
        this.productVariants.removeEventListener('VARIANT_CHANGE', this.handleVariantChange);
      }

      if (this.handleLinkClick) {
        this.removeEventListener('click', this.handleLinkClick);
      }

      if (this.handleLinkIntent) {
        this.removeEventListener('pointerover', this.handleLinkIntent);
        this.removeEventListener('focusin', this.handleLinkIntent);
        this.removeEventListener('touchstart', this.handleLinkIntent);
      }

      if (this.handleWindowLoad) {
        window.removeEventListener('load', this.handleWindowLoad);
      }

      if (this.handleScrollRestoreLoad) {
        window.removeEventListener('load', this.handleScrollRestoreLoad);
      }

      this.cancelBackgroundPrefetchSchedule();

      if (this.updateFrame) {
        cancelAnimationFrame(this.updateFrame);
      }
    }

    deferredInit() {
      this.attachVariantListener();
      this.updateLinks();
    }

    getProductVariants() {
      return this.productPage?.querySelector('product-variants') || null;
    }

    attachVariantListener() {
      const nextProductVariants = this.getProductVariants();

      if (!nextProductVariants || nextProductVariants === this.productVariants) return;

      if (this.productVariants && this.handleVariantChange) {
        this.productVariants.removeEventListener('VARIANT_CHANGE', this.handleVariantChange);
      }

      this.productVariants = nextProductVariants;
      this.productVariants.addEventListener('VARIANT_CHANGE', this.handleVariantChange);
    }

    getVariantMap() {
      const script = this.querySelector('[data-product-group-variant-map]');

      if (!script) return {};

      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    normalize(value) {
      return String(value || '').trim().toLowerCase();
    }

    getPreservedOptionGroup() {
      const productVariants = this.getProductVariants();
      if (!productVariants) return null;

      const targetName = this.normalize(this.dataset.preserveOptionName);

      return Array.from(
        productVariants.querySelectorAll('.product-variant[data-option-name]')
      ).find(
        (optionGroup) => this.normalize(optionGroup.dataset.optionName) === targetName
      ) || null;
    }

    getPreservedOptionValue() {
      const productVariants = this.getProductVariants();
      const optionGroup = this.getPreservedOptionGroup();

      if (!productVariants || !optionGroup) return '';

      // Primary source: the visible native option control.
      const select = optionGroup.querySelector('.product-variant-container');
      if (select?.value) return select.value;

      const checkedInput = optionGroup.querySelector('.product-variant__input:checked');
      if (checkedInput?.value) return checkedInput.value;

      // Fallback: Split's resolved currentVariant.
      const optionPosition = Number(optionGroup.dataset.optionPosition);
      const optionIndex = Number.isFinite(optionPosition) ? optionPosition - 1 : -1;

      if (
        optionIndex >= 0 &&
        productVariants.currentVariant?.options?.[optionIndex]
      ) {
        return productVariants.currentVariant.options[optionIndex];
      }

      // Final fallback: resolve Split's hidden Shopify variant ID against its
      // embedded variant JSON, then read the option by position.
      if (optionIndex >= 0) {
        const sectionId = productVariants.dataset.id;
        const variantInput = this.productPage?.querySelector(
          `#product-form-${sectionId} input[name="id"]`
        );
        const variantId = variantInput?.value;
        const variantJson = productVariants.querySelector('script[type="application/json"]');

        if (variantId && variantJson) {
          try {
            const variants = JSON.parse(variantJson.textContent || '[]');
            const currentVariant = variants.find(
              (variant) => String(variant.id) === String(variantId)
            );

            if (currentVariant?.options?.[optionIndex]) {
              return currentVariant.options[optionIndex];
            }
          } catch (error) {
            // Fail safely back to normal sibling product navigation.
          }
        }
      }

      return '';
    }

    getMatchingUrl(productMap, preservedValue) {
      if (!productMap || !preservedValue) return '';

      if (productMap[preservedValue]) {
        return productMap[preservedValue];
      }

      const normalizedValue = this.normalize(preservedValue);
      const matchingKey = Object.keys(productMap).find(
        (key) => this.normalize(key) === normalizedValue
      );

      return matchingKey ? productMap[matchingKey] : '';
    }

    updateLink(link, preservedValue) {
      const productMap = this.variantMap[String(link.dataset.productId)] || {};
      const matchingUrl = this.getMatchingUrl(productMap, preservedValue);
      const baseUrl = link.dataset.baseUrl || link.getAttribute('href');
      const nextUrl = matchingUrl || baseUrl;

      link.setAttribute('href', nextUrl);
      return nextUrl;
    }

    updateLinks() {
      this.attachVariantListener();

      const preservedValue = this.getPreservedOptionValue();

      this.links.forEach((link) => {
        this.updateLink(link, preservedValue);
      });
    }

    scheduleUpdate() {
      if (this.updateFrame) {
        cancelAnimationFrame(this.updateFrame);
      }

      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateLinks();

        // If a preserved native option (currently Beard Oil Size) changes,
        // warm the matching sibling URLs for that new selection as well.
        this.scheduleBackgroundPrefetch(VARIANT_PREFETCH_DELAY);
      });
    }

    onProductChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const changedInsideVariants = Boolean(target.closest('product-variants'));
      const changedVariantId =
        target.matches('input[name="id"]') &&
        Boolean(target.closest('form[id^="product-form-"]'));

      if (changedInsideVariants || changedVariantId) {
        this.scheduleUpdate();
      }
    }

    saveNavigationPosition(nextUrl) {
      if (!nextUrl) return;

      try {
        const targetUrl = new URL(nextUrl, window.location.origin);
        const selectorRect = this.getBoundingClientRect();
        const isVisible =
          selectorRect.bottom > 0 && selectorRect.top < window.innerHeight;

        // Only preserve viewport position for an intentional selector click
        // after the shopper has meaningfully scrolled. When the page is still
        // at/near the top, forcing the next document to scroll just to align a
        // selector below variable-length content creates the very jump we are
        // trying to avoid. The named View Transition handles that case instead.
        if (!isVisible || window.scrollY < SCROLL_MIN_Y) return;

        sessionStorage.setItem(
          SCROLL_STORAGE_KEY,
          JSON.stringify({
            targetPath: targetUrl.pathname,
            selectorTop: selectorRect.top,
            savedAt: Date.now()
          })
        );
      } catch (error) {
        // sessionStorage can be unavailable in restrictive privacy contexts.
      }
    }

    getStoredNavigationPosition() {
      try {
        const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
        if (!raw) return null;

        const stored = JSON.parse(raw);
        const isFresh =
          Number.isFinite(stored?.savedAt) &&
          Date.now() - stored.savedAt <= SCROLL_STORAGE_MAX_AGE;
        const matchesPath = stored?.targetPath === window.location.pathname;
        const hasPosition = Number.isFinite(stored?.selectorTop);

        // Consume the handoff immediately so refreshes or unrelated navigation
        // cannot replay an old scroll position.
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);

        if (!isFresh || !matchesPath || !hasPosition) {
          return null;
        }

        return stored;
      } catch (error) {
        return null;
      }
    }

    scheduleNavigationPositionRestore() {
      const stored = this.getStoredNavigationPosition();
      if (!stored) return;

      this.pendingNavigationPosition = stored;

      // Correct synchronously while this deferred script is initializing so
      // supporting browsers can capture the destination View Transition in its
      // aligned state instead of first painting at one position and then jumping.
      this.applyNavigationPosition(false);

      // A single next-frame correction catches layout that settles immediately
      // after custom-element setup without intentionally waiting through two
      // painted frames as the previous version did.
      requestAnimationFrame(() => this.applyNavigationPosition(false));

      if (document.readyState === 'complete') {
        this.applyNavigationPosition(true);
      } else {
        window.addEventListener('load', this.handleScrollRestoreLoad, { once: true });
      }
    }

    applyNavigationPosition(finalize = false) {
      const stored = this.pendingNavigationPosition;
      if (!stored || !this.isConnected) return;

      const currentTop = this.getBoundingClientRect().top;
      const delta = currentTop - stored.selectorTop;

      if (Number.isFinite(delta) && Math.abs(delta) > SCROLL_TOLERANCE) {
        window.scrollBy(0, delta);
      }

      if (finalize) {
        this.pendingNavigationPosition = null;
      }
    }

    savePreservedSelection(nextUrl, preservedValue) {
      if (!preservedValue || !nextUrl) return;

      try {
        const targetUrl = new URL(nextUrl, window.location.origin);

        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            optionName: this.dataset.preserveOptionName || '',
            optionValue: preservedValue,
            targetPath: targetUrl.pathname,
            savedAt: Date.now()
          })
        );
      } catch (error) {
        // sessionStorage can be unavailable in restrictive privacy contexts.
      }
    }

    getStoredSelection() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const stored = JSON.parse(raw);
        const isFresh =
          Number.isFinite(stored?.savedAt) &&
          Date.now() - stored.savedAt <= STORAGE_MAX_AGE;
        const matchesPath = stored?.targetPath === window.location.pathname;
        const matchesOption =
          this.normalize(stored?.optionName) ===
          this.normalize(this.dataset.preserveOptionName);

        if (!isFresh || !matchesPath || !matchesOption || !stored?.optionValue) {
          sessionStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return stored;
      } catch (error) {
        return null;
      }
    }

    restorePreservedSelection() {
      const stored = this.getStoredSelection();
      if (!stored) return;

      const optionGroup = this.getPreservedOptionGroup();
      if (!optionGroup) {
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (error) {}
        return;
      }

      const targetValue = this.normalize(stored.optionValue);
      const currentValue = this.normalize(this.getPreservedOptionValue());

      // Clear first so a variant-change event cannot cause this fallback to
      // re-run on the same page.
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {}

      if (!targetValue || currentValue === targetValue) {
        this.updateLinks();
        return;
      }

      const select = optionGroup.querySelector('.product-variant-container');
      if (select) {
        const matchingOption = Array.from(select.options).find(
          (option) => this.normalize(option.value) === targetValue
        );

        if (matchingOption) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      const matchingRadio = Array.from(
        optionGroup.querySelectorAll('.product-variant__input')
      ).find((input) => this.normalize(input.value) === targetValue);

      if (matchingRadio) {
        matchingRadio.checked = true;
        matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    prefetchUrl(nextUrl) {
      if (!nextUrl) return;

      try {
        const url = new URL(nextUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (this.prefetchedUrls.has(url.href)) return;

        const prefetch = document.createElement('link');
        prefetch.rel = 'prefetch';
        prefetch.href = url.href;
        prefetch.setAttribute('fetchpriority', 'low');
        prefetch.dataset.productGroupPrefetch = 'true';
        document.head.appendChild(prefetch);

        this.prefetchedUrls.add(url.href);
      } catch (error) {
        // Prefetch is optional; normal navigation remains the fallback.
      }
    }

    shouldBackgroundPrefetch() {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection?.saveData) return false;

      const effectiveType = String(connection?.effectiveType || '').toLowerCase();
      if (effectiveType === 'slow-2g' || effectiveType === '2g') return false;

      return true;
    }

    cancelBackgroundPrefetchSchedule() {
      if (this.backgroundPrefetchDelayTimer) {
        clearTimeout(this.backgroundPrefetchDelayTimer);
        this.backgroundPrefetchDelayTimer = null;
      }

      if (
        this.backgroundIdleCallback != null &&
        'cancelIdleCallback' in window
      ) {
        cancelIdleCallback(this.backgroundIdleCallback);
        this.backgroundIdleCallback = null;
      }

      this.backgroundPrefetchTimers.forEach((timer) => clearTimeout(timer));
      this.backgroundPrefetchTimers.clear();
    }

    scheduleBackgroundPrefetch(delay = BACKGROUND_PREFETCH_DELAY) {
      if (!this.shouldBackgroundPrefetch()) return;

      // Debounce repeated Split variant-change events so a Size change produces
      // one fresh background queue rather than several overlapping queues.
      this.cancelBackgroundPrefetchSchedule();

      this.backgroundPrefetchDelayTimer = window.setTimeout(() => {
        this.backgroundPrefetchDelayTimer = null;

        const run = () => {
          this.backgroundIdleCallback = null;
          this.prefetchSiblingUrls();
        };

        if ('requestIdleCallback' in window) {
          this.backgroundIdleCallback = requestIdleCallback(run, { timeout: 2000 });
        } else {
          run();
        }
      }, delay);
    }

    prefetchSiblingUrls() {
      if (!this.isConnected || !this.shouldBackgroundPrefetch()) return;

      const preservedValue = this.getPreservedOptionValue();
      const urls = this.links
        .map((link) => this.updateLink(link, preservedValue))
        .filter(Boolean);

      urls.forEach((url, index) => {
        const timer = window.setTimeout(() => {
          this.backgroundPrefetchTimers.delete(timer);

          if (!this.isConnected || !this.shouldBackgroundPrefetch()) return;
          this.prefetchUrl(url);
        }, index * BACKGROUND_PREFETCH_STAGGER);

        this.backgroundPrefetchTimers.add(timer);
      });
    }

    onLinkIntent(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest('[data-product-group-link]');
      if (!link || !this.contains(link)) return;

      // Re-resolve the URL at intent time so Beard Oil prefetches the currently
      // selected Size instead of whichever Size was selected on initial render.
      const preservedValue = this.getPreservedOptionValue();
      const nextUrl = this.updateLink(link, preservedValue);
      this.prefetchUrl(nextUrl);
    }

    onLinkClick(event) {
      const link = event.target.closest('[data-product-group-link]');
      if (!link || !this.contains(link)) return;

      const preservedValue = this.getPreservedOptionValue();
      const nextUrl = this.updateLink(link, preservedValue);

      this.savePreservedSelection(nextUrl, preservedValue);

      // For a normal left-click, explicitly navigate to the URL we just resolved.
      // Modified clicks (Cmd/Ctrl/Shift/Alt, middle-click, target=_blank) retain
      // normal browser behavior and use the already-updated href.
      const isPlainLeftClick =
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        (!link.target || link.target === '_self');

      if (isPlainLeftClick && nextUrl) {
        this.saveNavigationPosition(nextUrl);
        event.preventDefault();
        window.location.assign(nextUrl);
      }
    }
  }

  customElements.define('product-group-selector', ProductGroupSelector);
})();
